/**
 * Gedanken im Tagebuch (E14): mehrere je Tag, mit Fotos, optional zu einem
 * Termin. Privat — nie im Haushalt.
 *
 * Schlagwörter werden beim Speichern aus dem Text gelesen (`extractTags`); die
 * Suche (`noteMatches`) und „An diesem Tag" (`onThisDayDates`) rechnen in
 * `@kraftwerk/shared`.
 */

import { extractTags, newId, noteMatches, onThisDayDates } from '@kraftwerk/shared';
import type { NoteEventKind } from '@kraftwerk/shared';
import { saveJournal } from './journal.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalJournalNote, LocalJournalPhoto } from './local.js';
import type { PreparedPhoto } from './photos.js';
import { stamp, writeRow, writeRows } from './write.js';
import type { RowChange } from './write.js';

const live = <T extends { deletedAt: string | null }>(rows: T[]) =>
  rows.filter((row) => !row.deletedAt);

export interface NoteDraft {
  title: string | null;
  body: string;
  mood: number | null;
  time: string;
  event: { kind: NoteEventKind; id: string; title: string; date: string } | null;
}

export async function notesOfDay(
  date: string,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalNote[]> {
  return live(await database.journal_notes.where('date').equals(date).toArray()).sort((a, b) =>
    a.time.localeCompare(b.time),
  );
}

export async function saveNote(
  date: string,
  draft: NoteDraft,
  existingId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalNote> {
  const before = existingId ? await database.journal_notes.get(existingId) : undefined;
  // Der Tag soll in der Tagebuchliste stehen, auch wenn er nur Gedanken hat.
  if (!before) await saveJournal(date, {}, database);
  const title = draft.title?.trim() || null;
  return writeRow(
    'journal_notes',
    {
      ...before,
      id: existingId ?? newId(),
      date,
      time: draft.time,
      title,
      body: draft.body,
      mood: draft.mood,
      tags: extractTags(`${title ?? ''}\n${draft.body}`),
      eventKind: draft.event?.kind ?? null,
      eventId: draft.event?.id ?? null,
      eventTitle: draft.event?.title ?? null,
      eventDate: draft.event?.date ?? null,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

/** Gedanke löschen — seine Fotos gleich mit (weich, damit andere Geräte es erfahren). */
export async function deleteNote(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const note = await database.journal_notes.get(id);
  if (!note || note.deletedAt) return;
  const now = stamp();
  const photos = live(await database.journal_photos.where('noteId').equals(id).toArray());
  const changes: RowChange[] = [
    ...photos.map((photo): RowChange => ({
      table: 'journal_photos',
      row: { ...photo, updatedAt: now, deletedAt: now },
      kind: 'delete',
    })),
    { table: 'journal_notes', row: { ...note, updatedAt: now, deletedAt: now }, kind: 'delete' },
  ];
  await writeRows(changes, database);
  for (const photo of photos) await database.photo_blobs.delete(photo.id);
}

/**
 * Foto an einen Gedanken hängen. Es gehört zugleich zum Tag (`journalEntryId`)
 * — damit greifen Fotopflege, Sicherung und Kontingent wie bei jedem
 * Tagebuchfoto, und im Fotoraster steht es auch.
 */
export async function addNotePhoto(
  note: LocalJournalNote,
  photo: PreparedPhoto,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalPhoto> {
  const entry = await saveJournal(note.date, {}, database);
  const id = newId();
  await database.photo_blobs.put({ id, blob: photo.blob, createdAt: stamp(), attempts: 0 });
  return writeRow(
    'journal_photos',
    {
      id,
      updatedAt: stamp(),
      deletedAt: null,
      journalEntryId: entry.id,
      noteId: note.id,
      width: photo.width,
      height: photo.height,
      bytes: photo.blob.size,
      caption: null,
    },
    'upsert',
    database,
  );
}

export async function photosOfNote(
  noteId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalJournalPhoto[]> {
  return live(await database.journal_photos.where('noteId').equals(noteId).toArray()).sort((a, b) =>
    a.id.localeCompare(b.id),
  );
}

/** Gedanken, die ein Suchbegriff trifft — neueste zuerst. */
export async function searchNotes(
  query: string,
  database: KraftwerkDatabase = db,
  limit = 50,
): Promise<LocalJournalNote[]> {
  const rows = live(await database.journal_notes.toArray());
  return rows
    .filter((note) => noteMatches(note, query))
    .sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time))
    .slice(0, limit);
}

export interface Memory {
  date: string;
  kind: 'month' | 'year';
  ago: number;
  notes: LocalJournalNote[];
  photos: LocalJournalPhoto[];
}

/**
 * „An diesem Tag": Gedanken und Fotos derselben Kalenderstelle vor einem Monat
 * und in früheren Jahren. Nur Tage, an denen es etwas gibt.
 */
export async function memoriesFor(
  today: string,
  database: KraftwerkDatabase = db,
): Promise<Memory[]> {
  const targets = onThisDayDates(today);
  if (targets.length === 0) return [];
  const dates = targets.map((entry) => entry.date);
  const [notes, entries, photos] = await Promise.all([
    database.journal_notes.where('date').anyOf(dates).toArray(),
    database.journal_entries.where('date').anyOf(dates).toArray(),
    database.journal_photos.toArray(),
  ]);
  const dateOfEntry = new Map(live(entries).map((entry) => [entry.id, entry.date]));
  return targets
    .map((target) => ({
      ...target,
      notes: live(notes)
        .filter((note) => note.date === target.date)
        .sort((a, b) => a.time.localeCompare(b.time)),
      photos: live(photos).filter((photo) => dateOfEntry.get(photo.journalEntryId) === target.date),
    }))
    .filter((memory) => memory.notes.length > 0 || memory.photos.length > 0);
}

/** Gedanken zu einem Termin — für das Zeichen im Kalender. */
export async function notedEventIds(database: KraftwerkDatabase = db): Promise<Set<string>> {
  const rows = live(await database.journal_notes.toArray());
  return new Set(rows.flatMap((note) => (note.eventId ? [note.eventId] : [])));
}
